<template>
	<div class="signature-step">

		<!-- En-tête -->
		<div class="sig-header text-center">
			<div class="sig-hourglass bg-primary">
				<font-awesome-icon :icon="['far', 'hourglass']" />
			</div>
			<h5 class="font-weight-bold mb-0">{{ $t('monte.contrat_presque_finalise') }}</h5>
		</div>

		<!-- Étapes -->
		<div class="sig-steps">
			<!-- Étape 1 : faite -->
			<div class="sig-step-row">
				<div class="sig-step-badge bg-primary text-white">
					<font-awesome-icon :icon="['fal', 'file-invoice']" />
					<font-awesome-icon :icon="['fal', 'check-circle']" class="sig-step-check text-primary" />
				</div>
				<span class="sig-step-label font-weight-bold">{{ $t('monte.step_init_contrat') }}</span>
			</div>

			<!-- Étape 2 : en cours -->
			<div class="sig-step-row">
				<div class="sig-step-badge sig-step-badge-pending text-primary">
					<font-awesome-icon :icon="['fal', 'signature']" />
				</div>
				<div class="sig-step-content">
					<p class="font-weight-bold mb-0">{{ $t('monte.step_signature_reglement') }}</p>
					<p class="text-muted small mb-0">{{ $t('monte.veuillez_signer') }}</p>
				</div>
			</div>
		</div>

		<!-- Canvas de signature (pleine largeur sur mobile) -->
		<div class="sig-canvas-section" ref="canvasWrapper">
			<canvas
				ref="signatureCanvas"
				class="signature-canvas"
				@mousedown.prevent="startDraw"
				@mousemove="draw"
				@mouseup="stopDraw"
				@mouseleave="stopDraw"
				@touchstart.prevent="startDrawTouch"
				@touchmove.prevent="drawTouch"
				@touchend="stopDraw"
			/>
		</div>

		<!-- Actions -->
		<div class="sig-actions">
			<button
				type="button"
				class="btn btn-link"
				:disabled="signing"
				@click.prevent="clearCanvas"
			>
				{{ $t('monte.effacer_signature') }}
			</button>
			<button
				type="button"
				class="btn btn-primary"
				:disabled="!hasSignature || signing"
				@click.prevent="$emit('sign', getSignatureData())"
			>
				<b-spinner v-if="signing" small class="mr-1" />
				<font-awesome-icon v-else :icon="['fal', 'credit-card']" class="mr-1" />
				<template v-if="amount > 0">
					{{ $t('monte.signer_et_payer', { amount: formattedAmount }) }}
				</template>
				<template v-else>
					{{ $t('monte.signer_contrat') }}
				</template>
			</button>
		</div>

		<!-- Note sur l'acompte -->
		<p class="sig-note text-muted small text-center">
			<em>{{ $t('monte.info_validation_acompte') }}</em>
		</p>

		<!-- Besoin d'aide -->
		<div class="sig-help text-center">
			<p class="font-weight-bold mb-2">{{ $t('monte.besoin_aide') }}</p>
			<router-link :to="{ name: 'contactUs' }" class="btn btn-outline-primary">
				<font-awesome-icon :icon="['fal', 'envelope']" class="mr-1" />
				{{ $t('monte.contactez_nous') }}
			</router-link>
		</div>

	</div>
</template>

<script>
export default {
	name: 'SignatureStep',
	props: {
		/** Montant total à payer en euros (0 = signature seule sans paiement). */
		amount: {
			type: Number,
			default: 0,
		},
		/** Indique si la signature est en cours de traitement (désactive le bouton). */
		signing: {
			type: Boolean,
			default: false,
		},
	},
	emits: ['sign'],
	data() {
		return {
			drawing: false,
			hasSignature: false,
			ctx: null,
		}
	},
	computed: {
		/** Montant formaté en devise EUR selon la locale courante. */
		formattedAmount() {
			return this.priceFormat(this.amount, 'EUR', true)
		},
	},
	mounted() {
		this.$nextTick(() => {
			this.initCanvas()
		})
	},
	methods: {
		/**
		 * Initialise le contexte 2D du canvas de signature et adapte ses dimensions au conteneur.
		 * Sur mobile, le canvas prend toute la largeur avec une hauteur raisonnable.
		 */
		initCanvas() {
			const canvas = this.$refs.signatureCanvas
			if (!canvas) return
			const wrapper = this.$refs.canvasWrapper
			canvas.width  = wrapper.clientWidth || 300
			// Hauteur fixe pour un bon ratio largeur/hauteur
			canvas.height = 120
			this.ctx = canvas.getContext('2d')
			this.ctx.strokeStyle = '#333333'
			this.ctx.lineWidth   = 2
			this.ctx.lineCap     = 'round'
			this.ctx.lineJoin    = 'round'
		},
		startDraw(e) {
			this.drawing = true
			this.hasSignature = true
			const { x, y } = this.getCanvasPos(e)
			this.ctx.beginPath()
			this.ctx.moveTo(x, y)
		},
		draw(e) {
			if (!this.drawing) return
			const { x, y } = this.getCanvasPos(e)
			this.ctx.lineTo(x, y)
			this.ctx.stroke()
		},
		stopDraw() {
			this.drawing = false
		},
		startDrawTouch(e) {
			this.drawing = true
			this.hasSignature = true
			const { x, y } = this.getTouchPos(e)
			this.ctx.beginPath()
			this.ctx.moveTo(x, y)
		},
		drawTouch(e) {
			if (!this.drawing) return
			const { x, y } = this.getTouchPos(e)
			this.ctx.lineTo(x, y)
			this.ctx.stroke()
		},
		getCanvasPos(e) {
			const canvas = this.$refs.signatureCanvas
			const rect   = canvas.getBoundingClientRect()
			return {
				x: (e.clientX - rect.left) * (canvas.width  / rect.width),
				y: (e.clientY - rect.top)  * (canvas.height / rect.height),
			}
		},
		getTouchPos(e) {
			const canvas = this.$refs.signatureCanvas
			const rect   = canvas.getBoundingClientRect()
			const touch  = e.touches[0]
			return {
				x: (touch.clientX - rect.left) * (canvas.width  / rect.width),
				y: (touch.clientY - rect.top)  * (canvas.height / rect.height),
			}
		},
		clearCanvas() {
			const canvas = this.$refs.signatureCanvas
			this.ctx.clearRect(0, 0, canvas.width, canvas.height)
			this.hasSignature = false
		},
		/**
		 * Exporte le contenu du canvas sous forme de data URL PNG.
		 * @returns {string} Image base64 de la signature.
		 */
		getSignatureData() {
			return this.$refs.signatureCanvas.toDataURL('image/png')
		},
	},
}
</script>
